import { InventoryRecord } from '../types/inventory.types';
import {
  INV_STATUS_BRANCH_LABEL,
  INV_STATUS_WAREHOUSE_LABEL,
} from '../constants/inventory-status-labels';
import { formatDateTime } from './formatters/date';
import { formatCurrency } from './formatters/money';
import { formatWeightGrams } from './localeNumber';
import { computeInventoryRecordPricingTotals } from './inventoryRecordTotals';
import {
  buildPrintDocFooter,
  buildPrintDocHeader,
  buildPrintDocInfoField,
  escapeHtml,
  openPrintDocument,
  statusBadgeClassFromLabel,
  wrapPrintDocument,
} from './printDocument';

export { escapeHtml } from './printDocument';

export const getStatusBadgeClass = (status: string) => statusBadgeClassFromLabel(status);

export const formatStatus = (status: string) => {
  if (status === 'COMPLETED') return 'Completado';
  if (status === 'CANCELLED') return 'Cancelado';
  if (status === 'PENDING') return 'Pendiente';
  return status;
};

export const formatWarehouseStatus = (status: string) => {
  if (status === 'ASSIGNED') return INV_STATUS_BRANCH_LABEL;
  if (status === 'IN_TRANSIT') return 'En Tránsito';
  if (status === 'UNASSIGNED') return INV_STATUS_WAREHOUSE_LABEL;
  return status;
};

export const getWarehouseStatusClass = (status: string) => {
  if (status === 'ASSIGNED') return 'status-assigned';
  if (status === 'IN_TRANSIT') return 'status-in-transit';
  if (status === 'UNASSIGNED') return 'status-unassigned';
  return 'status-unassigned';
};

export const getWarehouseProductsForItem = (item: any, warehouseProducts?: any[]): any[] => {
  if (!warehouseProducts) return [];
  return warehouseProducts.filter((wp) => wp.productCode === item.product?.code);
};

const printCellDash = '—';

function formatPrintWeight(value: unknown): string {
  if (value == null || value === '' || Number.isNaN(Number(value))) {
    return printCellDash;
  }
  const formatted = formatWeightGrams(value as number | string);
  return formatted === 'No especificado' ? printCellDash : formatted;
}

function formatPrintMoney(value: unknown): string {
  if (value == null || value === '' || Number.isNaN(Number(value))) {
    return printCellDash;
  }
  return formatCurrency(value as number | string) || printCellDash;
}

function pieceWeightFromWarehouseProduct(wp: {
  weightGrams?: number;
  physicalAttributes?: Record<string, unknown>;
}): unknown {
  return wp.physicalAttributes?.weightGrams ?? wp.physicalAttributes?.weight ?? wp.weightGrams;
}

function pieceCostFromWarehouseProduct(wp: {
  costPrice?: number;
  pricing?: Record<string, unknown>;
}): unknown {
  return wp.pricing?.costPrice ?? wp.costPrice;
}

function pieceSaleFromWarehouseProduct(wp: {
  salePrice?: number;
  pricing?: Record<string, unknown>;
}): unknown {
  return wp.pricing?.salePrice ?? wp.salePrice;
}

/**
 * Print handler for inventory record details (LlamaOne document personality).
 */
export const printInventoryRecord = (record: InventoryRecord) => {
  if (!record) return;

  const productsHtml =
    record.items
      ?.map((item) => {
        const warehouseProducts = getWarehouseProductsForItem(item, record.warehouseProducts);
        const hasWarehouseProducts = warehouseProducts.length > 0;

        let warehouseProductsHtml = '';
        if (hasWarehouseProducts) {
          warehouseProductsHtml = `
        <div class="product-details">
          <div class="product-details-title">
            Piezas individuales (${warehouseProducts.length})
          </div>
          <table>
            <thead>
              <tr>
                <th>Código único</th>
                <th>Nombre</th>
                <th>Tipo</th>
                <th>Estado</th>
                <th>Material</th>
                <th>Peso</th>
                <th>Costo</th>
                <th>Precio de venta</th>
              </tr>
            </thead>
            <tbody>
              ${warehouseProducts
                .map(
                  (wp) => `
                <tr>
                  <td>${escapeHtml(wp.productCodeUnique) || '-'}</td>
                  <td>${escapeHtml(wp.productName) || '-'}</td>
                  <td>${escapeHtml(wp.productType) || '-'}</td>
                  <td>
                    <span class="${getWarehouseStatusClass(wp.status)}">
                      ${escapeHtml(formatWarehouseStatus(wp.status))}
                    </span>
                  </td>
                  <td>${
                    wp.material?.name || wp.material?.id
                      ? escapeHtml(wp.material?.name || wp.material?.id)
                      : 'N/A'
                  }</td>
                  <td>${escapeHtml(formatPrintWeight(pieceWeightFromWarehouseProduct(wp)))}</td>
                  <td>${escapeHtml(formatPrintMoney(pieceCostFromWarehouseProduct(wp)))}</td>
                  <td>${escapeHtml(formatPrintMoney(pieceSaleFromWarehouseProduct(wp)))}</td>
                </tr>
              `,
                )
                .join('')}
            </tbody>
          </table>
        </div>
      `;
        }

        return `
      <div class="product-item">
        <div class="product-name">${escapeHtml(item.product?.name || `Producto ${item.productId}`)}</div>
        <div class="product-code">${escapeHtml(item.product?.code) || '-'}</div>
        <div>
          <span class="product-quantity">${escapeHtml(item.quantity)} pza.</span>
        </div>
        ${warehouseProductsHtml}
      </div>
    `;
      })
      .join('') || '<div class="product-item">No hay líneas de producto en este registro</div>';

  const totalUnits = record.items?.reduce((sum, item) => sum + Number(item.quantity || 0), 0) || 0;
  const isCompleted = record.status === 'COMPLETED';
  const pricingTotals = isCompleted
    ? computeInventoryRecordPricingTotals(record.warehouseProducts)
    : null;

  const statusLabel = formatStatus(record.status || '');
  const bodyHtml = `
    ${buildPrintDocHeader('Detalles del Registro de Inventario', record.recordCode ?? undefined)}

    <div class="info-card">
      <div class="info-grid">
        ${buildPrintDocInfoField('Número de registro', escapeHtml(record.recordCode))}
        ${buildPrintDocInfoField(
          'Estado',
          `<span class="status-badge ${getStatusBadgeClass(record.status || '')}">${escapeHtml(statusLabel)}</span>`,
        )}
        ${buildPrintDocInfoField(
          'Ubicación',
          record.location ? escapeHtml(record.location) : 'N/A',
        )}
        ${buildPrintDocInfoField(
          'Fecha de creación',
          record.createdAt ? escapeHtml(formatDateTime(record.createdAt) || 'N/A') : 'N/A',
        )}
        ${buildPrintDocInfoField(
          'Fecha de registro',
          record.completedAt ? escapeHtml(formatDateTime(record.completedAt) || 'N/A') : 'N/A',
        )}
        ${buildPrintDocInfoField(
          'Registrado por',
          escapeHtml(
            record.registeredBy?.fullName?.trim() || record.registeredBy?.username?.trim() || 'N/A',
          ),
        )}
      </div>
    </div>

    <div class="section">
      <div class="section-header">Productos (${record.items?.length || 0})</div>
      ${productsHtml}
      ${
        record.items && record.items.length > 0
          ? `
        <div class="total-section">
          <div class="total-item">
            <span class="total-label">Total de piezas</span>
            <span class="total-value">${totalUnits} piezas</span>
          </div>
          ${
            pricingTotals
              ? `
          <div class="total-item">
            <span class="total-label">Costo total</span>
            <span class="total-value total-value-cost">${escapeHtml(formatCurrency(pricingTotals.totalCostPrice))}</span>
          </div>
          <div class="total-item">
            <span class="total-label">Precio de venta total</span>
            <span class="total-value total-value-sale">${escapeHtml(formatCurrency(pricingTotals.totalSalePrice))}</span>
          </div>
          `
              : ''
          }
        </div>
      `
          : ''
      }
    </div>

    ${buildPrintDocFooter()}
  `;

  openPrintDocument(
    wrapPrintDocument({
      documentTitle: `Detalles del Registro de Inventario - ${record.recordCode}`,
      bodyHtml,
    }),
    'printInventoryRecord',
  );
};
